/**
 * The tenant portal's own SMS consent popup — shown once, the first time a
 * tenant with no consent decision on file (status NOT_ASKED) reaches the
 * portal. Alongside, not instead of, the staff-recorded flow
 * (TenantForm.tsx's "Manage SMS consent" panel) — either path can record
 * the decision, and staff can still change it afterward if the tenant
 * calls in to update it; there is no tenant-facing "change later" control,
 * only this one-time prompt.
 *
 * Rendered only for a TENANT actor (gated in PortalLayout.tsx) — never for
 * OWNER or VENDOR, who have no SMS consent of their own.
 *
 * Shaped to Surge's opt-in requirements (docs.surge.app/concepts/
 * consent-flows and required-disclosures): the phone number, an explicit
 * checkbox that starts unchecked, and the required disclosures directly
 * beneath it in readable (not muted) text, with the Terms of Use and
 * Privacy Policy links. Opting in is optional — Save with the box unchecked
 * records DECLINED, and "Not now" records nothing.
 *
 * The disclosure is never composed here. The server builds it
 * (apps/api lib/sms-disclosures.ts) and returns it on the consent DTO; this
 * component renders that string as-is and echoes it back on save, so the
 * stored record is exactly what the tenant read.
 *
 * Not shown at all while the tenant has no phone number on file (the DTO's
 * `disclosure` is null): consent names the specific number it covers, and
 * there is no number to name yet. The tenant stays NOT_ASKED, so the prompt
 * appears on the first visit after staff add one.
 *
 * No reason field, unlike the staff-facing SmsConsentDialog: a staff
 * member's reason documents a judgment call made on someone else's behalf;
 * a tenant's own decision needs no justification. The service records a
 * sensible default reason on their behalf.
 */

import { useEffect, useState } from 'react';
import { fetchOwnSmsConsent, saveOwnSmsConsent, type SmsConsentDto } from '../lib/sms-consent.js';
import { FormErrors } from './Form.js';
import { Lightbox } from './Lightbox.js';

export function TenantSmsConsentPrompt() {
  const [consent, setConsent] = useState<SmsConsentDto | null>(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // Once resolved this visit, never show again even if a stale refetch
  // still reads NOT_ASKED — avoids the popup flashing back mid-transition.
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchOwnSmsConsent()
      .then((ownConsent) => {
        if (!cancelled) setConsent(ownConsent);
      })
      .catch(() => {
        // Best-effort — if this fails, the tenant just doesn't see the
        // popup this visit. Nothing else in the portal depends on it, and
        // staff can still record the decision from the tenant's profile.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (resolved || !consent || consent.status !== 'NOT_ASKED' || !consent.disclosure) return null;
  const disclosure = consent.disclosure;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await saveOwnSmsConsent({
        status: checked ? 'CONSENTED' : 'DECLINED',
        reason: undefined,
        disclosure,
      });
      setResolved(true);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Lightbox title="Text message updates" onClose={() => setResolved(true)}>
      <div className="stack">
        <p>
          Mobile number on file: <strong>{consent.phone}</strong>
        </p>

        <label className="apply-check-row">
          <input
            type="checkbox"
            checked={checked}
            disabled={busy}
            onChange={(event) => setChecked(event.target.checked)}
          />
          <span>Yes, text me about my lease and account.</span>
        </label>

        <p style={{ whiteSpace: 'pre-line' }}>{disclosure}</p>
        <p>
          <a href="/terms-of-use" target="_blank" rel="noreferrer">
            Terms of Use
          </a>{' '}
          ·{' '}
          <a href="/privacy-policy" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
        </p>

        <FormErrors error={error} />

        <div className="row">
          <button type="button" className="button" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="button button--quiet"
            disabled={busy}
            onClick={() => setResolved(true)}
          >
            Not now
          </button>
        </div>

        <p className="muted">
          You can change your answer later by contacting the office
          {consent.officePhone ? ` at ${consent.officePhone}` : ''}, or at any time by replying STOP
          or START to our texts.
        </p>
      </div>
    </Lightbox>
  );
}
